import { Component } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { withRouter, generatePath } from 'react-router-dom';
import FormattedMessage from 'I18n/components/FormattedMessage';
import styled from 'styled-components';
import getComponentName from 'react-utils/getComponentName';
import { NavMarker } from 'react-rhumb';
import memoize from 'transmute/memoize';
import UIPopover from 'UIComponents/tooltip/UIPopover';
import UIButton from 'UIComponents/button/UIButton';
import { parseLocation } from 'automation-ui-routing/utils';
import { getEdgeIdFromGoToActionId, isGoToActionId } from 'automation-ui-action-features/records/recordsUtils';
import { getObjectTypeId, getActionOutputsForTargetActionById } from 'automation-ui-canvas/flow/records/Flow/flowUtils';
import Trackables from 'automation-ui-usage-tracking/Trackables';
import * as UsageTrackingScreens from 'automation-ui-usage-tracking/UsageTrackingScreens';
import { trackPageView, trackEvent } from 'automation-ui-canvas/utils/trackingUtils';
import { ScreenTrackingContextProvider } from 'automation-ui-usage-tracking/ScreenTrackingContext';
import { updateFlowLocally } from 'automation-ui-canvas/actions/FlowActions';
import Action from 'automation-ui-action-features/records/Action';
import GoToActionCardWrapper from 'automation-ui-canvas/action-features/go-to/GoToActionCardWrapper';
import PannableDecorator from 'automation-ui-canvas/decorators/PannableDecorator';
import ZoomableDecorator from 'automation-ui-canvas/decorators/ZoomableDecorator';
import { translateToGraph } from 'automation-ui-canvas/utils/platformWorkflowTranslators';
import * as CanvasIdentifiers from 'automation-ui-flow-core/canvas/CanvasIdentifiers';
import * as ActionTypes from 'automation-ui-action-features/constants/ActionTypes';
import { attachWrappedComponent } from 'automation-ui-core/decoratorUtils';
import { getSourceApp } from 'automation-ui-canvas/selectors/embedSelectors';
import { getActionById } from 'automation-ui-canvas/flow/records/Flow/flowUtils';
import * as SpecialActionIds from 'automation-ui-flow-core/canvas/SpecialActionIds';
import FlowCanvasNode from 'automation-ui-canvas/components/canvas/FlowCanvasNode';
import { wireActionOutputs } from 'automation-ui-action-features/queries/actionOutputQueries';
import { wireAuthConsumer } from 'automation-ui-core/context/AuthContext';
import { wireUpdateActionAndSaveFlow, wireFlowById, defaultErrorHandlingForUpdateFlow } from 'automation-ui-canvas/queries/flowQueries';
import uniqueIdForObject from 'automation-ui-core/utils/uniqueIdForObject';
import { ALL_ACTIONS_LIST, CANVAS_ROOT } from 'automation-ui-canvas/constants/AbsolutePaths';
import { getScreenForFlowType } from 'automation-ui-canvas/utils/getScreenForFlowType';
import { buildSearchStringForActionSetRoutes } from 'automation-ui-canvas/utils/buildSearchStringForActionSetRoutes';
import { getZoomLevel } from 'automation-ui-canvas/selectors/localStorageSelectors';
import { DETAIL_MIN_ZOOM_LEVEL } from 'automation-ui-flow-core/canvas/graphConstants';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledCanvas = styled.div.withConfig({
  displayName: "FlowCanvasGoToEditDecorator__StyledCanvas",
  componentId: "bgvlq7-0"
})(["height:100%;"]);
function getComposedCanvas() {
  return PannableDecorator(
  // TODO(TS_BUG)
  // @ts-expect-error ZoomableDecorator expects StyledCanvas to have its required props, but it's just a div
  // and the required props aren't wired until ComposedCanvas is used in FlowCanvasBase. Not sure if real error or not
  ZoomableDecorator({
    WrappedComponent: StyledCanvas
  }));
}
export default function FlowCanvasGoToEditDecorator(WrappedComponent, centerOnMount) {
  const componentName = getComponentName(WrappedComponent);
  class FlowCanvasGoToEdit extends Component {
    constructor(...args) {
      super(...args);
      this.state = {
        actionIdsForGoToAction: undefined,
        goToHoverNode: {},
        selectedGoToActionId: null,
        currentStateFromProps: {
          flowId: this.props.flowId,
          flow: this.props.flow
        }
      };
      this.ComposedCanvas = getComposedCanvas();
      this.keydownEventListener = event => {
        const {
          key
        } = event;
        if (key === 'Escape') {
          this.redirectToFullCanvas();
        }
      };
      this.setEscapeKeyEventListener = () => {
        document.addEventListener('keydown', this.keydownEventListener);
      };
      this.removeEscapeKeyEventListener = () => {
        document.removeEventListener('keydown', this.keydownEventListener);
      };
      this.getComposedProps = () => {
        const {
          flow,
          canvasScrollElementRef,
          currentActionId,
          zoomLevel
        } = this.props;
        return {
          flow,
          onClickAddControl: this.onClickAddControl,
          canvasScrollElementRef,
          onRenderCanvasNode: this.onRenderCanvasNode,
          onSelectAction: this.onSelectAction,
          ComposedCanvas: this.ComposedCanvas,
          goToHoverNode: this.state.goToHoverNode,
          goToEditNodeId: currentActionId,
          selectedGoToActionId: this.state.selectedGoToActionId,
          // EJM - disable end markers for action sets, undefined keeps the default behavior
          onRenderEndMarker: flow.isActionSet() ? () => null : undefined,
          centerOnMount,
          zoomLevel
        };
      };
      this.handleGetActionOutputsForTargetActionById = (targetActionId, isNew) => getActionOutputsForTargetActionById({
        flow: this.props.flow,
        allOutputsByActionTypeId: this.props.allOutputsByActionTypeId,
        instanceOutputsByActionId: this.props.instanceOutputsByActionId,
        targetActionId,
        isNewAction: isNew
      });
      this.handleOnMouseEnter = (id, fromNode = SpecialActionIds.NEW_ACTION) => () => {
        this.setState({
          goToHoverNode: {
            fromNode,
            toNode: id,
            isHover: fromNode === SpecialActionIds.NEW_ACTION
          }
        });
      };
      this.handleOnMouseLeave = isSelected => () => {
        if (isSelected) {
          return;
        }
        this.setState({
          goToHoverNode: {}
        });
      };
      this.toggleAddEmptyActionToFlow = () => {
        const {
          flow,
          dispatch,
          edgeId,
          currentActionId
        } = this.props;
        if (!flow || currentActionId !== SpecialActionIds.NEW_ACTION) {
          return;
        }
        const emptyAction = new Action({
          actionType: ActionTypes.EMPTY_ACTION,
          actionId: SpecialActionIds.NEW_ACTION
        });
        let newFlow = flow;
        newFlow = newFlow.removeAction(SpecialActionIds.NEW_ACTION);
        newFlow = newFlow.insertAction({
          action: emptyAction,
          edgeId,
          actionId: SpecialActionIds.NEW_ACTION
        });
        dispatch(updateFlowLocally(newFlow));
      };
      this.onRenderPopoverBody = () => {
        return /*#__PURE__*/_jsx("h5", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "automation.goToEditCanvas.saveConnectionPopover.message"
          })
        });
      };
      this.onRenderPopoverFooter = id => {
        return /*#__PURE__*/_jsxs("div", {
          children: [/*#__PURE__*/_jsx(UIButton, {
            use: "primary",
            onClick: this.onPopoverSave(id),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "automation.goToEditCanvas.saveConnectionPopover.doneLabel"
            })
          }), /*#__PURE__*/_jsx(UIButton, {
            use: "secondary",
            onClick: this.onPopoverCancel,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "automation.goToEditCanvas.saveConnectionPopover.cancelLabel"
            })
          })]
        });
      };
      this.onRenderCanvasNode = memoize(({
        id: nodeId,
        width,
        height
      }) => {
        const {
          flow,
          currentActionId,
          showEnrollmentCard,
          zoomLevel
        } = this.props;
        const {
          actionIdsForGoToAction,
          selectedGoToActionId
        } = this.state;
        const canGoToAction = actionIdsForGoToAction.includes(nodeId);
        const isSelected = nodeId === selectedGoToActionId;
        if (nodeId === CanvasIdentifiers.FIRST_NODE && !showEnrollmentCard) {
          return null;
        } else if (isGoToActionId(nodeId) || nodeId === SpecialActionIds.NEW_ACTION) {
          return /*#__PURE__*/_jsx(GoToActionCardWrapper, {
            flow: flow,
            isSelected: false,
            isHighlighted: nodeId === currentActionId,
            isDisabled: true,
            disabledDescription: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "automation.goToEditCanvas.disabledNodeTooltipGoToAction"
            }),
            width: width,
            height: height,
            actionId: nodeId,
            layout: zoomLevel < DETAIL_MIN_ZOOM_LEVEL ? 'zoomed-out' : 'default'
          });
        }
        const content = /*#__PURE__*/_jsx(FlowCanvasNode, {
          id: nodeId,
          width: width,
          height: height,
          flow: flow,
          isHighlighted: nodeId === currentActionId,
          isDisabled: !canGoToAction,
          onSelectNode: this.onSelectAction,
          getActionOutputsForTargetActionById: this.handleGetActionOutputsForTargetActionById,
          readOnly: true,
          disabledDescription: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "automation.goToEditCanvas.disabledNodeTooltip"
          }),
          zoomLevel: zoomLevel
        });
        let contents = content;
        let mouseEvents = {};
        if (canGoToAction) {
          mouseEvents = {
            onMouseEnter: this.handleOnMouseEnter(nodeId, currentActionId),
            onMouseLeave: this.handleOnMouseLeave(isSelected)
          };
          contents = /*#__PURE__*/_jsx(UIPopover, {
            open: isSelected,
            closeOnOutsideClick: true,
            placement: "bottom",
            content: {
              body: this.onRenderPopoverBody(),
              footer: this.onRenderPopoverFooter(nodeId)
            },
            onOpenChange: ({
              target: {
                value
              }
            }) => {
              if (!value) {
                this.onPopoverCancel();
              }
            },
            children: content
          });
        }
        return /*#__PURE__*/_jsx("div", Object.assign({}, mouseEvents, {
          children: contents
        }));
      }, ({
        id: nodeId
      }) => `${nodeId}-${uniqueIdForObject(this.props.flow)}-${this.props.flowId}-${this.props.currentActionId}-${this.state.selectedGoToActionId}-${this.state.actionIdsForGoToAction}-${this.props.zoomLevel}`);
      this.onPopoverCancel = event => {
        if (event) {
          event.stopPropagation();
        }
        const {
          flow
        } = this.props;
        trackEvent(Trackables.INTERACT_WITH_GO_TO_ACTION, {
          interactionType: 'cancel (editor)',
          objectTypeId: getObjectTypeId(flow)
        });
        this.setState({
          selectedGoToActionId: null,
          goToHoverNode: {}
        });
      };
      this.onPopoverSave = id => {
        const {
          previousAction,
          updateActionAndSaveFlow,
          sourceApp,
          flow,
          updateConnection,
          edgeId,
          currentActionId,
          dispatch
        } = this.props;
        const correctedEdgeId = edgeId || (currentActionId ? getEdgeIdFromGoToActionId(currentActionId) : null);
        return event => {
          event.stopPropagation();
          const newFlow = flow.removeAction(SpecialActionIds.NEW_ACTION);
          const previousActionId = previousAction.getActionId();
          let newAction = newFlow.getActionById(previousActionId);
          if (updateConnection) {
            newAction = newAction.removeEdgeFromConnection(correctedEdgeId);
          }
          newAction = newAction.addEdgeToConnection(correctedEdgeId, id, true);
          trackEvent(Trackables.INTERACT_WITH_GO_TO_ACTION, {
            interactionType: updateConnection ? 'save' : 'create',
            objectTypeId: getObjectTypeId(flow)
          });
          updateActionAndSaveFlow({
            flow: newFlow,
            action: newAction,
            edgeId,
            sourceApp
          }).then(this.redirectToFullCanvas).catch(error => defaultErrorHandlingForUpdateFlow({
            error,
            dispatch
          }));
        };
      };
      this.onSelectAction = id => {
        const actionId = id;
        const canGoToAction = this.state.actionIdsForGoToAction.includes(actionId);
        if (!canGoToAction) {
          return;
        }
        this.setState({
          selectedGoToActionId: actionId
        });
      };
      this.onClickAddControl = edgeId => {
        const {
          history,
          location,
          flowId,
          edgeId: selectedEdgeId,
          flow
        } = this.props;
        const removeEdgeId = selectedEdgeId === edgeId;
        if (removeEdgeId) {
          return this.redirectToFullCanvas();
        }
        if (!removeEdgeId) {
          trackEvent(Trackables.ADD_ACTION_CONFIG, {
            objectTypeId: getObjectTypeId(flow)
          });
        }
        const queryParams = {
          edgeId
        };
        const search = buildSearchStringForActionSetRoutes(flow, location, queryParams);
        const screen = getScreenForFlowType(flow.flowType);
        const pathname = generatePath(ALL_ACTIONS_LIST, {
          flowId,
          screen
        });
        return history.replace({
          pathname,
          search
        });
      };
      this.redirectToFullCanvas = () => {
        const {
          flowId,
          history,
          location,
          flow
        } = this.props;
        const screen = getScreenForFlowType(flow.flowType);
        const pathname = generatePath(CANVAS_ROOT, {
          flowId,
          screen
        });
        const search = buildSearchStringForActionSetRoutes(flow, location);
        return history.replace({
          pathname,
          search
        });
      };
    }
    static getDerivedStateFromProps(props, state) {
      const {
        flowId,
        flow,
        previousActionId,
        currentActionId
      } = props;
      const {
        currentStateFromProps
      } = state;
      if (flowId !== currentStateFromProps.flowId) {
        FlowCanvasGoToEdit.handleRequestFlow(props);
      }
      let actionIdsForGoToAction = state.actionIdsForGoToAction;
      if (flow !== currentStateFromProps.flow || !actionIdsForGoToAction) {
        actionIdsForGoToAction = FlowCanvasGoToEdit.setValidGoToActionIds(flow, previousActionId, currentActionId);
      }
      return {
        actionIdsForGoToAction,
        currentStateFromProps: {
          flowId,
          flow
        }
      };
    }
    componentDidMount() {
      this.setEscapeKeyEventListener();
      this.toggleAddEmptyActionToFlow();
      trackPageView(UsageTrackingScreens.PLATFORM_FLOW_GO_TO_EDIT_CONTAINER);
    }
    componentWillUnmount() {
      this.removeEscapeKeyEventListener();
    }
    render() {
      const {
        flow
      } = this.props;

      // TODO: handle error fetching flow and add to Raegan
      if (!flow) {
        return null;
      }
      return /*#__PURE__*/_jsx(NavMarker, {
        name: "GO_TO_EDIT_CANVAS",
        children: /*#__PURE__*/_jsx(ScreenTrackingContextProvider, {
          value: UsageTrackingScreens.PLATFORM_FLOW_GO_TO_EDIT_CONTAINER,
          children: /*#__PURE__*/_jsx(WrappedComponent, Object.assign({}, this.props, this.getComposedProps()))
        })
      });
    }
  }
  FlowCanvasGoToEdit.getActionIdsForGoToAction = flowAsGraph => memoize((previousActionId, currentActionId) => {
    const edgeId = currentActionId.split(`-${SpecialActionIds.GO_TO}`)[0];
    return flowAsGraph.getNodeIdsForGoToNode(previousActionId, edgeId);
  }, (previousActionId, currentActionId) => `${previousActionId}-${currentActionId}`);
  FlowCanvasGoToEdit.setValidGoToActionIds = (flow, previousActionId, currentActionId) => {
    if (!flow) {
      return [];
    }

    // creates edges graph with GoTo edges
    // so that we can accurately find cycles
    const flowAsGraph = translateToGraph(flow, true);
    const actionIdsForGoToAction = FlowCanvasGoToEdit.getActionIdsForGoToAction(flowAsGraph)(previousActionId, currentActionId);
    return actionIdsForGoToAction;
  };
  FlowCanvasGoToEdit.handleRequestFlow = ({
    flowQuery,
    flowId
  }) => {
    if (flowId > 0) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      flowQuery.refetch({
        flowId
      });
    }
  };
  const ConnectedFlowCanvasGoToEdit = compose(withRouter, wireAuthConsumer, _WrappedComponent => props => {
    const {
      flowId: stringId
    } = props.match.params;
    const flowId = Number(stringId);
    return /*#__PURE__*/_jsx(_WrappedComponent, Object.assign({}, props, {
      flowId: flowId
    }));
  }, wireFlowById, connect((state, ownProps) => {
    const {
      flowId,
      flow,
      match,
      location
    } = ownProps;
    const {
      actionId: currentActionId
    } = match.params;
    const parsedQuery = parseLocation(location);
    const {
      edgeId,
      updateConnection
    } = parsedQuery;
    const previousActionIdModifier = currentActionId === SpecialActionIds.NEW_ACTION ? edgeId : currentActionId;
    const previousActionId = previousActionIdModifier ? previousActionIdModifier.split('-edge-')[0] : null;
    const previousAction = getActionById(flow, Number(previousActionId));
    const showEnrollmentCard = !flow || !flow.isActionSet();
    return {
      flow,
      flowId,
      previousActionId,
      previousAction,
      currentActionId,
      updateConnection: updateConnection === 'true',
      edgeId,
      activeFlowId: Number(parsedQuery.activeFlowId),
      sourceApp: getSourceApp(state),
      showEnrollmentCard,
      zoomLevel: getZoomLevel(flowId)(state)
    };
  }, dispatch => ({
    dispatch
  })), wireUpdateActionAndSaveFlow, _WrappedComponent => props => wireActionOutputs(_WrappedComponent, {
    flow: props.flow
  })(props))(FlowCanvasGoToEdit);
  ConnectedFlowCanvasGoToEdit.displayName = `ConnectedFlowCanvasGoToEdit(${componentName})`;
  attachWrappedComponent(ConnectedFlowCanvasGoToEdit, WrappedComponent);
  return ConnectedFlowCanvasGoToEdit;
}